package com.essntl.features.itinerary.ui.edit_itinerary.components.messaging

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.supplier.domain.model.SupplierModel
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.chat.contact.ContactUiState.ContactType
import com.essntl.uikit.components.chat.group.EsGroupMemberCard
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.theme.esColorScheme

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MessageGroupPopup(
    openDialog: Boolean,
    onDismissRequest: () -> Unit,
    primaryClient: ClientModel?,
    clients: List<ClientModel>,
    travelAgent: TravelAgentModel?,
    suppliers: List<SupplierModel>,
    itineraryServiceList: List<ItineraryServiceModel>,
    popupOffset: Offset = Offset.Zero,
    groupSubject: String?,
    onSend: (String, List<String>) -> Unit,
) {
    val contacts = itineraryServiceList.mapNotNull { itineraryService ->
        val contact = itineraryService.supplierContact

        if ((contact?.firstName.isNullOrEmpty() &&
                contact?.lastName.isNullOrEmpty()) ||
            contact.phoneNumber.isNullOrEmpty()
        )
            null
        else
            ClientModel.empty.copy(
                lastName = contact.prettyName,
                telephone = contact.phoneNumber,
            )
    }

    if (openDialog) {
        BasicAlertDialog(
            onDismissRequest = onDismissRequest,
            properties = DialogProperties(
                usePlatformDefaultWidth = false,
                dismissOnClickOutside = false,
            ),
        ) {
            MessageGroupPopupUi(
                primaryClient = primaryClient,
                clients = clients,
                travelAgent = travelAgent,
                suppliers = suppliers,
                contacts = contacts,
                groupSubject = groupSubject,
                onDismissRequest = onDismissRequest,
                onSend = onSend,
            )
        }
    }
}

@Composable
fun MessageGroupPopupUi(
    primaryClient: ClientModel?,
    clients: List<ClientModel>,
    travelAgent: TravelAgentModel?,
    suppliers: List<SupplierModel>,
    contacts: List<ClientModel>,
    groupSubject: String?,
    onDismissRequest: () -> Unit,
    onSend: (String, List<String>) -> Unit,
    modifier: Modifier = Modifier,
) {
    val groupMembers = remember { mutableStateOf(listOf(primaryClient?.telephone ?: "")) }
    var editGroupSubject by remember { mutableStateOf(groupSubject ?: "") }

    Column(
        modifier = modifier
            .border(
                width = 1.dp,
                color = MaterialTheme.esColorScheme.borderPrimary,
                shape = RoundedCornerShape(8.dp),
            )
            .background(MaterialTheme.esColorScheme.bgPrimary, shape = RoundedCornerShape(8.dp)),
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            EsSmallOutlineIconButton(
                onClick = onDismissRequest,
                iconRes = EsIcons.Close,
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(8.dp),
            )
        }

        LazyColumn(
            verticalArrangement = Arrangement.spacedBy(4.dp),
            contentPadding = PaddingValues(
                start = 24.dp,
                end = 24.dp,
                bottom = 18.dp,
            ),
        ) {
            item {
                EsInput(
                    value = editGroupSubject,
                    onValueChange = {
                        editGroupSubject = it
                    },
                    placeholder = groupSubject,
                    showClearButton = false,
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(modifier = Modifier.height(12.dp))
            }

            if (primaryClient != null)
                item {
                    EsGroupMemberCard(
                        name = primaryClient.prettyName,
                        phoneNumber = primaryClient.telephone,
                        type = ContactType.Client,
                        isChecked = groupMembers.value.contains(primaryClient.telephone),
                        onCheck = { isChecked ->
                            val phoneNumber = primaryClient.telephone ?: return@EsGroupMemberCard
                            groupMembers.value = if (isChecked)
                                groupMembers.value + phoneNumber
                            else
                                groupMembers.value - phoneNumber
                        },
                    )
                }

            clients.forEach { client ->
                item {
                    EsGroupMemberCard(
                        name = client.prettyName,
                        phoneNumber = client.telephone,
                        type = ContactType.Client,
                        isChecked = groupMembers.value.contains(client.telephone),
                        onCheck = { isChecked ->
                            val phoneNumber = client.telephone ?: return@EsGroupMemberCard
                            groupMembers.value = if (isChecked)
                                groupMembers.value + phoneNumber
                            else
                                groupMembers.value - phoneNumber
                        },
                    )
                }
            }

            if (travelAgent != null)
                item {
                    EsGroupMemberCard(
                        name = travelAgent.prettyName,
                        phoneNumber = travelAgent.contact?.phoneNumber,
                        type = ContactType.TravelAgent,
                        isChecked = groupMembers.value.contains(travelAgent.contact?.phoneNumber),
                        onCheck = { isChecked ->
                            val phoneNumber = travelAgent.contact?.phoneNumber ?: return@EsGroupMemberCard
                            groupMembers.value = if (isChecked)
                                groupMembers.value + phoneNumber
                            else
                                groupMembers.value - phoneNumber
                        },
                    )
                }

            contacts.forEach { contact ->
                item {
                    EsGroupMemberCard(
                        name = contact.prettyName,
                        phoneNumber = contact.telephone,
                        type = ContactType.Supplier,
                        isChecked = groupMembers.value.contains(contact.telephone),
                        onCheck = { isChecked ->
                            val phoneNumber = contact.telephone ?: return@EsGroupMemberCard
                            groupMembers.value = if (isChecked)
                                groupMembers.value + phoneNumber
                            else
                                groupMembers.value - phoneNumber
                        },
                    )
                }
            }

            suppliers.forEach { supplier ->
                item {
                    EsGroupMemberCard(
                        name = supplier.contactName ?: supplier.prettyName,
                        phoneNumber = supplier.contact?.phoneNumber,
                        type = ContactType.Supplier,
                        isChecked = groupMembers.value.contains(supplier.contact?.phoneNumber),
                        onCheck = { isChecked ->
                            val phoneNumber = supplier.contact?.phoneNumber ?: return@EsGroupMemberCard
                            groupMembers.value = if (isChecked)
                                groupMembers.value + phoneNumber
                            else
                                groupMembers.value - phoneNumber
                        },
                    )
                }
            }

            item {
                Spacer(modifier = Modifier.height(12.dp))
                EsLargePrimaryButton(
                    onClick = {
                        onSend(
                            editGroupSubject.ifEmpty { "Be Essential Club" },
                            groupMembers.value,
                        )
                        onDismissRequest()
                    },
                    text = "Create group & send",
                    enabled = groupMembers.value.isNotEmpty(),
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}